/**
 * AnimateTeaser — mini-sheet shown when a free user taps Animate.
 *
 * Per iter-73: animation is a Premium feature. Spec says don't dump them
 * straight into the pricing modal — show a small "motion is premium"
 * teaser first that sets the aspiration (drift / breathe / shimmer /
 * glow are real motion treatments, not gimmicks), then escalate to
 * the full PremiumModal so they see ALL the benefits.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Play, Lock } from "lucide-react";
import { track } from "../../lib/track";

const MOTION_TYPES = [
    { name: "drift",   sub: "background slowly drifts — gives the scene life" },
    { name: "breathe", sub: "the card softly inhales — like a sigh" },
    { name: "shimmer", sub: "type catches light — premium poster vibe" },
    { name: "glow",    sub: "warm edge glow — cinematic depth" },
];

export default function AnimateTeaser({ open, onClose, onUnlock }) {
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm"
                        data-testid="animate-teaser-backdrop"
                    />
                    <motion.div
                        initial={{ y: 80, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 80, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed left-0 right-0 bottom-0 z-50 px-3 pb-5 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:px-4 sm:pb-0 pointer-events-none"
                        data-testid="animate-teaser"
                    >
                        <div className="pointer-events-auto mx-auto w-full max-w-md rounded-3xl bg-[#0f0f12] border border-white/10 shadow-2xl overflow-hidden">
                            {/* Cinematic header strip with motion preview */}
                            <div className="relative h-28 bg-gradient-to-br from-violet-500/40 via-sky-500/30 to-amber-400/30 flex items-end justify-between p-4 overflow-hidden">
                                <motion.div
                                    aria-hidden
                                    className="absolute inset-0"
                                    animate={{
                                        background: [
                                            "radial-gradient(circle at 20% 30%, rgba(167,139,250,0.45), transparent 50%)",
                                            "radial-gradient(circle at 80% 70%, rgba(56,189,248,0.45), transparent 50%)",
                                            "radial-gradient(circle at 20% 30%, rgba(167,139,250,0.45), transparent 50%)",
                                        ],
                                    }}
                                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                                />
                                <div className="relative flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-medium px-2.5 py-1 rounded-full bg-black/55 text-white/90 backdrop-blur-sm">
                                    <Lock className="w-3 h-3" />
                                    <span>premium · motion</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="relative w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white/90 hover:bg-black/75 transition-colors"
                                    aria-label="close"
                                    data-testid="animate-teaser-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="p-5 sm:p-6">
                                <div className="text-[11px] tracking-[0.22em] uppercase text-amber-300/85 mb-1.5">
                                    let your card breathe
                                </div>
                                <h3 className="text-xl sm:text-2xl font-display text-white">
                                    Animate this feeling
                                </h3>
                                <p className="mt-2 text-sm text-white/55 leading-relaxed">
                                    Four cinematic motion treatments — designed to feel like a poster you'd see on a screen, not a meme GIF.
                                </p>

                                <ul className="mt-4 space-y-2.5">
                                    {MOTION_TYPES.map((m) => (
                                        <li key={m.name} className="flex items-start gap-3" data-testid={`animate-teaser-motion-${m.name}`}>
                                            <div className="w-7 h-7 shrink-0 rounded-full bg-violet-400/10 border border-violet-300/25 flex items-center justify-center mt-0.5">
                                                <Sparkles className="w-3.5 h-3.5 text-violet-200" />
                                            </div>
                                            <div>
                                                <div className="text-sm text-white/90 font-medium lowercase">{m.name}</div>
                                                <div className="text-xs text-white/45 leading-snug mt-0.5">{m.sub}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-6 flex flex-col gap-2">
                                    <button
                                        type="button"
                                        onClick={() => { track("animate_teaser_unlock"); onUnlock?.(); }}
                                        className="w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-medium text-sm tracking-wide transition-colors flex items-center justify-center gap-2"
                                        data-testid="animate-teaser-unlock"
                                    >
                                        <Play className="w-4 h-4" />
                                        unlock motion · see all premium perks
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { track("animate_teaser_dismiss"); onClose?.(); }}
                                        className="w-full py-2.5 rounded-full text-white/55 hover:text-white/85 text-sm transition-colors"
                                        data-testid="animate-teaser-dismiss"
                                    >
                                        maybe later
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
